import * as React from 'react';
import { Modal, Button, Alert, Typography, Divider } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import './EditDiscountActionDialog.css';
import { getAllCurrencies } from 'app/pages/DiscountEditor/modules/RefData';
import {
  Currency,
  DiscountValueType,
  DiscountActionTargetTypeEnum,
} from 'app/shared/model';
import {
  getDiscountActionToEdit,
  DiscountTarget,
  EditableDiscountBand,
  EditableBandedDiscount,
  getFees,
} from 'app/pages/DiscountEditor/modules/DiscountActions';
import {
  cancelAddOrUpdateDiscountAction,
  updateEditableBadedDiscount,
} from 'app/pages/DiscountEditor/actions';
import { saveDiscountActionCommand, changeDiscountActionCurrencyCommand, changeDiscountActionValueTypeCommand } from 'app/pages/DiscountEditor/commands';
import { BandsEditor } from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/BandsEditor';
import { DiscountTargetEditor } from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/DiscountTargetEditor';
import { Fee } from 'app/shared/model/Fee';
import { SelectionMap, ProductFilter } from 'app/shared/model/ProductFilter';
import { Action } from 'redux';

const { Title, Text } = Typography;

interface BandedDiscountDialogProps {}

interface StateProps {
  errorMessage?: string;
  discountAction?: EditableBandedDiscount;
  allCurrencies: Currency[];
  currencyCode: string;
  currencySymbol: string;
  readOnly: boolean;
  fees: Fee[];
}

interface DispatchProps {
  cancel: () => void;
  save: () => void;
  changeValueType: (value: any) => void;
  changeCurrency: (currencyCode: string) => void;
  onUpdateBands: (
    discountAction: Readonly<EditableBandedDiscount>,
    bands: Readonly<EditableDiscountBand>[]
  ) => void;
  changeFees: (
    action: Readonly<EditableBandedDiscount>,
    selection: SelectionMap
  ) => void;
  changeProductFilter: (
    action: Readonly<EditableBandedDiscount>,
    selection: ProductFilter
  ) => void;
}

type InternalProps = StateProps & DispatchProps;

const BandedDiscountDialog: React.FC<InternalProps> = props => {
  if (!props.discountAction) return null;

  const {
    errorMessage,
    discountAction,
    allCurrencies,
    currencyCode,
    currencySymbol,
    readOnly,
    fees,
    cancel,
    save,
    changeValueType,
    changeCurrency,
    onUpdateBands,
    changeFees,
    changeProductFilter,
  } = props;

  return (
    <Modal
      open={true}
      title="Discount action"
      className="banded-discount-dialog"
      onCancel={cancel}
      footer={[
        <Button key="cancel" onClick={cancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<CheckOutlined />}
          onClick={save}
          disabled={readOnly}
        >
          Save
        </Button>,
      ]}
      mask={{ closable: false }}
      closable
      destroyOnHidden
      width={800}
    >
      {errorMessage && (
        <Alert
          type="error"
          title={errorMessage}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

    <Text type="secondary" style={{ display: 'block', marginTop: 24, marginBottom: 24 }}>
      <DiscountTargetEditor
        discountTarget={discountAction.target}
        fees={fees}
        feeSelectionChanged={sel => changeFees(discountAction, sel)}
        productFilterChanged={sel => changeProductFilter(discountAction, sel)}
      />
    </Text>
    <Divider style={{ margin: '24px 0' }} />

      <Title level={5} style={{ marginBottom: 8, fontWeight: 600, color: '#262626' }}>
        Discount details:
      </Title>

      <BandsEditor
        changeValueType={changeValueType}
        changeCurrency={changeCurrency}
        bandType={discountAction.bandType}
        bands={discountAction.bands}
        onUpdateBands={b => onUpdateBands(discountAction, b)}
        valueType={discountAction.discountValueType}
        showValidation={discountAction.showValidation}
        readOnly={readOnly}
        allCurrencies={allCurrencies}
        currencyCode={currencyCode}
        currencySymbol={currencySymbol}
      />
    </Modal>
  );
};

function mapStateToProps(
  state: any,
  ownProps: BandedDiscountDialogProps
): StateProps {
  const discountToEdit = getDiscountActionToEdit(state);
  const allCurrencies = getAllCurrencies(state) ?? [];
  const valueType = discountToEdit
    ? discountToEdit.discountValueType
    : DiscountValueType.AmountPerUnit;

  const savedCurrencyCode = discountToEdit?.currencyCode;
  const currency = allCurrencies.find(c => c.code === savedCurrencyCode)
    ?? allCurrencies[0];
  const currencyCode = currency?.code ?? '';
  const currencySymbol = currency?.symbol ?? '';
  

  return {
    // Currency is selected from dropdown (all system currencies); no school/centre validation
    errorMessage: undefined,
    readOnly: false,
    allCurrencies,
    currencyCode,
    currencySymbol,
    discountAction: discountToEdit,
    fees: getFees(state),
  };
}

function mapDispatchToProps(
  dispatch: ThunkDispatch<any, any, Action>,
  ownProps: BandedDiscountDialogProps
): DispatchProps {
  return {
    cancel: () => dispatch(cancelAddOrUpdateDiscountAction()),
    save: () => dispatch(saveDiscountActionCommand()),
    changeValueType: value => dispatch(changeDiscountActionValueTypeCommand(value)),
    changeCurrency: (currencyCode: string) =>
      dispatch(changeDiscountActionCurrencyCommand(currencyCode)),
    onUpdateBands: (discountAction, bands) =>
      dispatch(
        updateEditableBadedDiscount({
          ...discountAction,
          bands,
        })
      ),
    changeFees: (discountAction, selection) =>
      dispatch(
        updateEditableBadedDiscount({
          ...discountAction,
          target: {
            type: DiscountActionTargetTypeEnum.Fees,
            fees: selection,
          },
        })
      ),
    changeProductFilter: (discountAction, selection) =>
      dispatch(
        updateEditableBadedDiscount({
          ...discountAction,
          target: {
            type: DiscountActionTargetTypeEnum.OtherProducts,
            products: selection,
          },
        })
      ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BandedDiscountDialog);
